import jsPDF from 'jspdf';
import {
  DocumentTemplate,
  TemplateBlock,
  TemplateFontSize,
  TemplateListBlock,
  TemplateTextAlign,
  TemplateTextStyle,
  parseTemplateListText,
  resolvePlaceholderText,
} from '../models/document-template.type';
import { loadLogoForPdf } from './prescription-pdf.util';
import {
  TemplateTextRun,
  parseTemplateRichText,
  plainTextFromTemplateRichText,
} from './template-rich-text.util';

/** Matches jsPDF `format: 'letter'` in mm. */
export const TEMPLATE_PAGE_WIDTH_MM = 215.9;
export const TEMPLATE_PAGE_HEIGHT_MM = 279.4;
export const TEMPLATE_PAGE_MARGIN_MM = 18;
export const TEMPLATE_HEADER_DIVIDER_GAP_MM = 10;

/** Body text font — keep editor CSS in sync with jsPDF. */
export const TEMPLATE_BODY_FONT_FAMILY = 'Helvetica, Arial, sans-serif';
export const TEMPLATE_BODY_FONT_SIZE_PT: Record<TemplateFontSize, number> = {
  sm: 10,
  md: 12,
  lg: 16,
};
/**
 * PDF advances Y by `fontSizePt * this` mm per line.
 * CSS unitless line-height ≈ this * (72 / 25.4) ≈ 1.2756.
 */
export const TEMPLATE_LINE_HEIGHT_MM_PER_PT = 0.45;
export const TEMPLATE_LINE_HEIGHT_CSS =
  TEMPLATE_LINE_HEIGHT_MM_PER_PT * (72 / 25.4);
export const TEMPLATE_BLOCK_GAP_AFTER_TEXT_MM = 3;
export const TEMPLATE_SIGNATURE_FONT_SIZE_PT = 11;

const MARGIN = TEMPLATE_PAGE_MARGIN_MM;
const LOGO_MAX_WIDTH_FRACTION = 0.2;
const LOGO_MAX_HEIGHT_FRACTION = 0.1;
const HEADER_NAME_GAP = 6;
const HEADER_NAME_FONT_SIZE = 20;
const HEADER_NAME_LINE_HEIGHT = 8;

export interface TemplatePdfContext {
  template: DocumentTemplate;
  values: Record<string, string>;
  businessName: string;
  logo?: Awaited<ReturnType<typeof loadLogoForPdf>>;
}

const FONT_SIZE_PT = TEMPLATE_BODY_FONT_SIZE_PT;

/**
 * Y position (mm from page top) where template body content starts in the PDF.
 * Keep the editor canvas in sync with this.
 */
export function estimateContentStartYMm(
  includeBusinessHeader: boolean,
  options?: {
    businessName?: string;
    logo?: { width: number; height: number } | null;
  }
): number {
  if (!includeBusinessHeader) {
    return MARGIN;
  }

  const pageWidth = TEMPLATE_PAGE_WIDTH_MM;
  const pageHeight = TEMPLATE_PAGE_HEIGHT_MM;
  const name = (options?.businessName || 'Consultorio').trim() || 'Consultorio';
  const logo = options?.logo;

  let headerBottom: number;
  if (!logo) {
    const maxWidth = pageWidth - MARGIN * 2;
    const lines = estimateWrappedLineCount(name, maxWidth, HEADER_NAME_FONT_SIZE);
    headerBottom = MARGIN + lines * HEADER_NAME_LINE_HEIGHT + 4;
  } else {
    const maxLogoWidth = pageWidth * LOGO_MAX_WIDTH_FRACTION;
    const maxLogoHeight = pageHeight * LOGO_MAX_HEIGHT_FRACTION;
    const ratio = Math.min(
      maxLogoWidth / logo.width,
      maxLogoHeight / logo.height,
      1
    );
    const logoHeight = logo.height * ratio;
    const nameX = MARGIN + logo.width * ratio + HEADER_NAME_GAP;
    const nameMaxWidth = pageWidth - MARGIN - nameX;
    const nameLines = estimateWrappedLineCount(
      name,
      Math.max(nameMaxWidth, 20),
      HEADER_NAME_FONT_SIZE
    );
    const textBlockHeight = nameLines * HEADER_NAME_LINE_HEIGHT;
    const headerHeight = Math.max(logoHeight, textBlockHeight);
    headerBottom = MARGIN + headerHeight + 6;
  }

  return headerBottom + TEMPLATE_HEADER_DIVIDER_GAP_MM;
}

/** Rough wrap estimate for Helvetica bold ~fontSize pt in mm widths. */
function estimateWrappedLineCount(
  text: string,
  maxWidthMm: number,
  fontSizePt: number
): number {
  const avgCharMm = fontSizePt * 0.22;
  const charsPerLine = Math.max(8, Math.floor(maxWidthMm / avgCharMm));
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) {
    return 1;
  }
  let lines = 1;
  let used = 0;
  for (const word of words) {
    const next = used === 0 ? word.length : used + 1 + word.length;
    if (next > charsPerLine && used > 0) {
      lines += 1;
      used = word.length;
    } else {
      used = next;
    }
  }
  return Math.max(1, lines);
}

export function buildTemplateDocumentPdf(context: TemplatePdfContext): jsPDF {
  const pdf = new jsPDF({ format: 'letter', unit: 'mm' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;

  let y = MARGIN;
  if (context.template.includeBusinessHeader !== false) {
    y = drawHeader(pdf, context.businessName, context.logo, pageWidth, pageHeight);
    pdf.setDrawColor(210, 210, 210);
    pdf.setLineWidth(0.4);
    pdf.line(MARGIN, y, pageWidth - MARGIN, y);
    y += TEMPLATE_HEADER_DIVIDER_GAP_MM;
  }

  for (const block of context.template.blocks) {
    y = drawBlock(pdf, block, context.values, MARGIN, y, contentWidth, pageHeight);
  }

  return pdf;
}

function drawBlock(
  pdf: jsPDF,
  block: TemplateBlock,
  values: Record<string, string>,
  x: number,
  y: number,
  width: number,
  pageHeight: number
): number {
  switch (block.type) {
    case 'text': {
      const text = block.content.trimEnd();
      if (!plainTextFromTemplateRichText(text).trim()) {
        return y + 2;
      }
      return drawStyledText(pdf, text, block.style, x, y, width, pageHeight, values);
    }
    case 'placeholder': {
      const text = values[block.key]?.trim() || `{{${block.key}}}`;
      return drawStyledText(pdf, text, block.style, x, y, width, pageHeight, values);
    }
    case 'spacer':
      return ensureSpace(pdf, y, block.heightMm, pageHeight) + block.heightMm;
    case 'divider': {
      y = ensureSpace(pdf, y, 8, pageHeight);
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(x, y + 3, x + width, y + 3);
      return y + 8;
    }
    case 'signature': {
      y = ensureSpace(pdf, y, 28, pageHeight);
      const lineWidth = Math.min(width, 70);
      const lineX = x + (width - lineWidth) / 2;
      pdf.setDrawColor(40, 40, 40);
      pdf.setLineWidth(0.4);
      pdf.line(lineX, y + 16, lineX + lineWidth, y + 16);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(TEMPLATE_SIGNATURE_FONT_SIZE_PT);
      pdf.setTextColor(50, 50, 50);
      const label = block.label?.trim() || 'Firma';
      const labelWidth = pdf.getTextWidth(label);
      pdf.text(label, x + (width - labelWidth) / 2, y + 22);
      return y + 28;
    }
    case 'list':
      return drawListBlock(pdf, block, values, x, y, width, pageHeight);
  }
}

const LIST_CARD_PADDING_MM = 4;
const LIST_CARD_GAP_MM = 5;
const LIST_TITLE_FONT_SIZE_PT = 12;
const LIST_DETAIL_FONT_SIZE_PT = 10;
const LIST_TITLE_LINE_MM = 5;
const LIST_DETAIL_LINE_MM = 4;

function drawListBlock(
  pdf: jsPDF,
  block: TemplateListBlock,
  values: Record<string, string>,
  x: number,
  y: number,
  width: number,
  pageHeight: number
): number {
  const items = parseTemplateListText(values[block.key]);
  items.forEach((item, index) => {
    y =
      block.listStyle === 'cards'
        ? drawListCard(pdf, index + 1, item.title, item.detail, x, y, width, pageHeight)
        : drawListLine(
            pdf,
            block.listStyle === 'numbered' ? `${index + 1}. ` : '•  ',
            item.title,
            item.detail,
            x,
            y,
            width,
            pageHeight
          );
  });
  return items.length ? y : y + 2;
}

function drawListCard(
  pdf: jsPDF,
  index: number,
  title: string,
  detail: string | undefined,
  x: number,
  y: number,
  width: number,
  pageHeight: number
): number {
  const padding = LIST_CARD_PADDING_MM;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(LIST_TITLE_FONT_SIZE_PT);
  const titleLines: string[] = pdf.splitTextToSize(`${index}. ${title}`, width - padding * 2);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(LIST_DETAIL_FONT_SIZE_PT);
  const detailLines: string[] = detail
    ? pdf.splitTextToSize(detail, width - padding * 2)
    : [];

  const blockHeight =
    padding * 2 +
    titleLines.length * LIST_TITLE_LINE_MM +
    (detailLines.length ? detailLines.length * LIST_DETAIL_LINE_MM + 2 : 0);
  y = ensureSpace(pdf, y, blockHeight + 10, pageHeight);

  pdf.setFillColor(248, 248, 248);
  pdf.setDrawColor(225, 225, 225);
  pdf.setLineWidth(0.4);
  pdf.roundedRect(x, y, width, blockHeight, 2, 2, 'FD');

  let textY = y + padding + 4;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(LIST_TITLE_FONT_SIZE_PT);
  pdf.setTextColor(25, 25, 25);
  pdf.text(titleLines, x + padding, textY);

  textY += titleLines.length * LIST_TITLE_LINE_MM;
  if (detailLines.length) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(LIST_DETAIL_FONT_SIZE_PT);
    pdf.setTextColor(90, 90, 90);
    pdf.text(detailLines, x + padding, textY + 1);
  }

  return y + blockHeight + LIST_CARD_GAP_MM;
}

function drawListLine(
  pdf: jsPDF,
  prefix: string,
  title: string,
  detail: string | undefined,
  x: number,
  y: number,
  width: number,
  pageHeight: number
): number {
  const fontSize = FONT_SIZE_PT.md;
  const lineHeight = fontSize * TEMPLATE_LINE_HEIGHT_MM_PER_PT;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(fontSize);
  const indent = pdf.getTextWidth(prefix);
  const titleLines: string[] = pdf.splitTextToSize(title, width - indent);

  pdf.setFontSize(LIST_DETAIL_FONT_SIZE_PT);
  const detailLines: string[] = detail ? pdf.splitTextToSize(detail, width - indent) : [];
  const height =
    titleLines.length * lineHeight + detailLines.length * LIST_DETAIL_LINE_MM;
  y = ensureSpace(pdf, y, height + 1, pageHeight);

  pdf.setFontSize(fontSize);
  pdf.setTextColor(30, 30, 30);
  pdf.text(prefix, x, y);
  titleLines.forEach((line) => {
    pdf.text(line, x + indent, y);
    y += lineHeight;
  });
  if (detailLines.length) {
    pdf.setFontSize(LIST_DETAIL_FONT_SIZE_PT);
    pdf.setTextColor(90, 90, 90);
    detailLines.forEach((line) => {
      pdf.text(line, x + indent, y - 1);
      y += LIST_DETAIL_LINE_MM;
    });
  }
  return y + 2;
}

function drawStyledText(
  pdf: jsPDF,
  text: string,
  style: TemplateTextStyle,
  x: number,
  y: number,
  width: number,
  pageHeight: number,
  values: Record<string, string> = {}
): number {
  const fontSize = FONT_SIZE_PT[style.fontSize];
  const lineHeight = fontSize * TEMPLATE_LINE_HEIGHT_MM_PER_PT;
  pdf.setFontSize(fontSize);
  pdf.setTextColor(30, 30, 30);

  const runs = parseTemplateRichText(text).map((run) => ({
    text: resolvePlaceholderText(run.text, values),
    style: {
      bold: run.style.bold || style.bold,
      italic: run.style.italic,
      underline: run.style.underline,
    },
  }));
  const lines = wrapRichTextRuns(pdf, runs, width);

  for (const line of lines) {
    y = ensureSpace(pdf, y, lineHeight + 1, pageHeight);
    const lineWidth = measureRichLine(pdf, line);
    let cursorX = alignedRichX(x, width, lineWidth, style.align);
    for (const run of line) {
      if (!run.text) {
        continue;
      }
      applyPdfFont(pdf, run.style);
      pdf.text(run.text, cursorX, y);
      const runWidth = pdf.getTextWidth(run.text);
      if (run.style.underline) {
        const underlineY = y + fontSize * 0.15;
        pdf.setDrawColor(30, 30, 30);
        pdf.setLineWidth(0.2);
        pdf.line(cursorX, underlineY, cursorX + runWidth, underlineY);
      }
      cursorX += runWidth;
    }
    y += lineHeight;
  }
  return y + TEMPLATE_BLOCK_GAP_AFTER_TEXT_MM;
}

function applyPdfFont(
  pdf: jsPDF,
  style: { bold: boolean; italic: boolean; underline: boolean }
): void {
  let face: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal';
  if (style.bold && style.italic) {
    face = 'bolditalic';
  } else if (style.bold) {
    face = 'bold';
  } else if (style.italic) {
    face = 'italic';
  }
  pdf.setFont('helvetica', face);
}

function wrapRichTextRuns(
  pdf: jsPDF,
  runs: TemplateTextRun[],
  maxWidth: number
): TemplateTextRun[][] {
  const lines: TemplateTextRun[][] = [];
  let current: TemplateTextRun[] = [];
  let used = 0;

  const pushLine = () => {
    lines.push(current);
    current = [];
    used = 0;
  };

  const measure = (text: string, style: TemplateTextRun['style']) => {
    applyPdfFont(pdf, style);
    return pdf.getTextWidth(text);
  };

  const appendRun = (text: string, style: TemplateTextRun['style']) => {
    if (!text) {
      return;
    }
    // Drop leading whitespace at the start of a wrapped line.
    if (used === 0 && /^\s+$/.test(text)) {
      return;
    }
    const width = measure(text, style);
    if (used > 0 && used + width > maxWidth) {
      pushLine();
      if (/^\s+$/.test(text)) {
        return;
      }
    }
    if (measure(text, style) > maxWidth && text.length > 1) {
      let chunk = '';
      for (const ch of text) {
        const next = chunk + ch;
        if (measure(next, style) > maxWidth && chunk) {
          current.push({ text: chunk, style: { ...style } });
          pushLine();
          chunk = ch;
        } else {
          chunk = next;
        }
      }
      if (chunk) {
        current.push({ text: chunk, style: { ...style } });
        used = measure(chunk, style);
      }
      return;
    }

    current.push({ text, style: { ...style } });
    used += measure(text, style);
  };

  for (const run of runs) {
    const parts = run.text.split(/(\n)/);
    for (const part of parts) {
      if (part === '\n') {
        pushLine();
        continue;
      }
      const words = part.split(/(\s+)/);
      for (const word of words) {
        appendRun(word, run.style);
      }
    }
  }

  if (current.length || !lines.length) {
    lines.push(current);
  }
  return lines;
}

function measureRichLine(pdf: jsPDF, line: TemplateTextRun[]): number {
  let width = 0;
  for (const run of line) {
    applyPdfFont(pdf, run.style);
    width += pdf.getTextWidth(run.text);
  }
  return width;
}

function alignedRichX(
  x: number,
  width: number,
  lineWidth: number,
  align: TemplateTextAlign
): number {
  if (align === 'center') {
    return x + (width - lineWidth) / 2;
  }
  if (align === 'right') {
    return x + width - lineWidth;
  }
  return x;
}

function ensureSpace(
  pdf: jsPDF,
  y: number,
  needed: number,
  pageHeight: number
): number {
  if (y + needed <= pageHeight - MARGIN) {
    return y;
  }
  pdf.addPage();
  return MARGIN;
}

function drawHeader(
  pdf: jsPDF,
  businessName: string,
  logo: TemplatePdfContext['logo'],
  pageWidth: number,
  pageHeight: number
): number {
  const name = businessName || 'Consultorio';
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(HEADER_NAME_FONT_SIZE);
  pdf.setTextColor(30, 30, 30);

  if (!logo) {
    const lines = pdf.splitTextToSize(name, pageWidth - MARGIN * 2);
    pdf.text(lines, MARGIN, MARGIN);
    return MARGIN + Math.max(lines.length, 1) * HEADER_NAME_LINE_HEIGHT + 4;
  }

  const maxLogoWidth = pageWidth * LOGO_MAX_WIDTH_FRACTION;
  const maxLogoHeight = pageHeight * LOGO_MAX_HEIGHT_FRACTION;
  const ratio = Math.min(
    maxLogoWidth / logo.width,
    maxLogoHeight / logo.height,
    1
  );
  const logoSize = {
    width: logo.width * ratio,
    height: logo.height * ratio,
  };

  pdf.addImage(
    logo.dataUrl,
    logo.format,
    MARGIN,
    MARGIN,
    logoSize.width,
    logoSize.height
  );

  const nameX = MARGIN + logoSize.width + HEADER_NAME_GAP;
  const nameMaxWidth = pageWidth - MARGIN - nameX;
  const nameLines = pdf.splitTextToSize(name, nameMaxWidth);
  const textBlockHeight = nameLines.length * HEADER_NAME_LINE_HEIGHT;
  const textStartY =
    MARGIN + (logoSize.height - textBlockHeight) / 2 + HEADER_NAME_LINE_HEIGHT * 0.35;
  pdf.text(nameLines, nameX, textStartY);

  const headerHeight = Math.max(logoSize.height, textBlockHeight);
  return MARGIN + headerHeight + 6;
}
